import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Check, Crown } from '@phosphor-icons/react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const StudioPassSuccessPage = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [status, setStatus] = useState('pending'); // pending | active | failed
  const [attempts, setAttempts] = useState(0);

  const poll = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/studio-pass/status`, { withCredentials: true });
      if (res.data?.active) {
        setStatus('active');
        return true;
      }
    } catch (_) { /* continue polling */ }
    return false;
  }, []);

  useEffect(() => {
    let stopped = false;
    const tick = async () => {
      if (stopped) return;
      const ok = await poll();
      if (ok) return;
      setAttempts((a) => a + 1);
      if (attempts >= 12) {
        setStatus('failed');
        return;
      }
      setTimeout(tick, 2500);
    };
    tick();
    return () => { stopped = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-[#0F1014] text-white flex items-center justify-center px-6" data-testid="studio-pass-success">
      <div className="max-w-md text-center">
        <div
          className="w-16 h-16 mx-auto rounded-2xl flex items-center justify-center mb-6"
          style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
        >
          {status === 'active' ? (
            <Check size={30} weight="bold" className="text-white" />
          ) : (
            <Crown size={26} weight="fill" className="text-white" />
          )}
        </div>

        {status === 'pending' && (
          <>
            <h1 className="text-2xl font-bold mb-2">Activating your Studio Pass…</h1>
            <p className="text-sm text-gray-400">
              We're confirming your subscription with Stripe. This usually takes a few seconds.
            </p>
          </>
        )}
        {status === 'active' && (
          <>
            <h1 className="text-2xl font-bold mb-2">Welcome to Studio Pass 🎉</h1>
            <p className="text-sm text-gray-400 mb-6">
              Every event you create from now on gets Pro features auto-unlocked — no more per-event upsells.
            </p>
            <div className="flex gap-2 justify-center">
              <button
                onClick={() => navigate('/my-events')}
                className="px-4 h-11 rounded-xl font-semibold text-white text-sm"
                style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
                data-testid="studio-pass-success-cta"
              >
                Go to my events
              </button>
              <button
                onClick={() => navigate('/event/create')}
                className="px-4 h-11 rounded-xl font-semibold text-white text-sm border border-white/15 hover:bg-white/5"
              >
                Create new event
              </button>
            </div>
          </>
        )}
        {status === 'failed' && (
          <>
            <h1 className="text-2xl font-bold mb-2">Still processing…</h1>
            <p className="text-sm text-gray-400 mb-4">
              Your subscription may take a moment to sync. Give it a couple of minutes and check your dashboard — if it still hasn't appeared, email support with session <code className="text-gray-300">{sessionId || 'unknown'}</code>.
            </p>
            <button
              onClick={() => navigate('/my-events')}
              className="px-4 h-11 rounded-xl font-semibold text-white text-sm border border-white/15 hover:bg-white/5"
            >
              Go to my events
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default StudioPassSuccessPage;
